import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

const TOAST_CFG = {
  success: {
    accent: '#10b981',
    bg:     'rgba(16,185,129,0.12)',
    border: 'rgba(16,185,129,0.35)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round"
        style={{ animation: 'wt-check-draw 0.5s ease forwards' }}>
        <style>{`
          @keyframes wt-check-draw {
            0%   { stroke-dashoffset: 40; opacity: 0; }
            30%  { opacity: 1; }
            100% { stroke-dashoffset: 0; }
          }
          .wt-check-path { stroke-dasharray: 40; stroke-dashoffset: 40; animation: wt-check-draw 0.45s ease 0.05s forwards; }
          @keyframes wt-circle-pop { 0%{transform:scale(0.5);opacity:0} 70%{transform:scale(1.15)} 100%{transform:scale(1);opacity:1} }
          .wt-check-circle { animation: wt-circle-pop 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        `}</style>
        <circle cx="12" cy="12" r="10" className="wt-check-circle" fill="rgba(16,185,129,0.2)" stroke="rgba(16,185,129,0.8)" strokeWidth="1.5"/>
        <polyline points="7 12.5 10.5 16 17 9" className="wt-check-path" stroke="#10b981"/>
      </svg>
    ),
  },
  error: {
    accent: '#f43f5e',
    bg:     'rgba(244,63,94,0.10)',
    border: 'rgba(244,63,94,0.32)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" fill="rgba(244,63,94,0.15)" stroke="rgba(244,63,94,0.7)" strokeWidth="1.5"/>
        <line x1="15" y1="9" x2="9" y2="15"/>
        <line x1="9" y1="9" x2="15" y2="15"/>
      </svg>
    ),
  },
  warning: {
    accent: '#f59e0b',
    bg:     'rgba(245,158,11,0.10)',
    border: 'rgba(245,158,11,0.32)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          fill="rgba(245,158,11,0.15)" stroke="rgba(245,158,11,0.7)" strokeWidth="1.5"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
  },
  info: {
    accent: '#0ABAB5',
    bg:     'rgba(10,186,181,0.10)',
    border: 'rgba(10,186,181,0.32)',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" fill="rgba(10,186,181,0.15)" stroke="rgba(10,186,181,0.7)" strokeWidth="1.5"/>
        <line x1="12" y1="16" x2="12" y2="12" stroke="#0ABAB5"/>
        <line x1="12" y1="8" x2="12.01" y2="8" stroke="#0ABAB5"/>
      </svg>
    ),
  },
};

function ToastItem({ t }) {
  const cfg = TOAST_CFG[t.type] || TOAST_CFG.info;
  return (
    <div
      key={t.id}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.65rem',
        padding: '0.65rem 0.85rem',
        borderRadius: '0.875rem',
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: `0 4px 24px rgba(0,0,0,0.22), 0 0 0 1px ${cfg.border}`,
        animation: 'wt-toast-in 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        fontFamily: "'Cairo', sans-serif",
      }}
    >
      <div style={{ width: 26, height: 26, flexShrink: 0 }}>{cfg.icon}</div>
      <span style={{
        fontSize: '0.82rem', fontWeight: 700, lineHeight: 1.4,
        color: 'var(--wt-text-primary, rgba(240,249,255,0.95))',
        flex: 1,
      }}>
        {t.message}
      </span>
    </div>
  );
}

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const show = useCallback((message, type = 'info', duration = 3500) => {
    const id = Date.now();
    setToasts(p => [...p, { id, message, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), duration);
  }, []);

  return (
    <ToastContext.Provider value={{
      success: m => show(m, 'success'),
      error:   m => show(m, 'error'),
      warning: m => show(m, 'warning'),
      info:    m => show(m, 'info'),
    }}>
      {children}
      <style>{`
        @keyframes wt-toast-in {
          from { opacity:0; transform:translateY(-10px) scale(0.95); }
          to   { opacity:1; transform:translateY(0)     scale(1);    }
        }
      `}</style>
      <div style={{
        position: 'fixed', top: '4.5rem', left: '50%', transform: 'translateX(-50%)',
        zIndex: 99999, display: 'flex', flexDirection: 'column', gap: '0.5rem',
        width: 'calc(100vw - 2rem)', maxWidth: '22rem', pointerEvents: 'none',
      }}>
        {toasts.map(t => <ToastItem key={t.id} t={t} />)}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
